import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../store'
import { Bug, AlertTriangle, ArrowRight, ListChecks, Search, ShieldCheck } from 'lucide-react'
import { Link } from './nav'
import { isResolved } from '../../netlify/functions/lib/bug-management/statuses'
import type { Bug as BugType } from '../data/projectTypes'
import type { TestSession } from '../data/projectTypes'
import BugTable from './bug-management/BugTable'
import TestRunTable from './TestRunTable'
import { useGetProjectSecurityResultsQuery, useGetPlaywrightAnalysesQuery, useGetUserSettingsQuery } from '../services/projectDataApi'
import { permanentHttpDenial } from '../lib/permanentHttpDenial'
import './OverviewSummary.css'

function StatCard({
  icon,
  label,
  value,
  to,
  loading = false,
  loadingValue,
}: {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
  to?: string
  loading?: boolean
  /** Static fallback value rendered invisibly while loading so the line reserves its loaded height. */
  loadingValue?: React.ReactNode
}) {
  const inner = (
    <>
      <div className="overview-summary__card-head">
        <span className="overview-summary__card-label">{label}</span>
        <span className="overview-summary__card-icon" aria-hidden>{icon}</span>
      </div>
      {loading ? (
        <span className="overview-summary__card-value-line" role="status" aria-label={`Loading ${label}`}>
          {loadingValue != null && (
            <span className="overview-summary__card-value-ghost" aria-hidden>{loadingValue}</span>
          )}
          <span className="overview-summary__card-value-skeleton" aria-hidden />
        </span>
      ) : (
        <span className="overview-summary__card-value">{value}</span>
      )}
    </>
  )
  if (loading) return <div className="overview-summary__card" aria-busy="true">{inner}</div>
  if (to) {
    return (
      <Link to={to} className="overview-summary__card overview-summary__card--clickable" data-analytics-event="project-overview-stat-card">
        {inner}
      </Link>
    )
  }
  return <div className="overview-summary__card">{inner}</div>
}

// The stat-card row: what the project found, plus the surfaces that only exist for some projects.
export function OverviewStatCards({
  projectId,
  openCount,
  testRunTotal,
  testRunLoading,
  bugCountLoading,
  securityEnabled,
  continuousTesting = false,
}: {
  projectId: string
  openCount: number
  testRunTotal: number
  testRunLoading: boolean
  bugCountLoading: boolean
  /** Security only exists once the project runs the security polish pass. */
  securityEnabled: boolean
  continuousTesting?: boolean
}) {

  if (continuousTesting) return <PlaywrightInfoCard projectId={projectId} />

  // The card links to exactly the open reports it counts.
  const openBugsHref = `/projects/${projectId}/bugs?status=open`

  return (
    <div className="overview-summary__cards" data-testid="overview-stat-cards">
      <StatCard icon={<Bug size={18} />} label="Bugs" value={openCount} to={openBugsHref} loading={bugCountLoading} />
      <StatCard icon={<ListChecks size={18} />} label="Test runs" value={testRunTotal} to={`/projects/${projectId}/test-runs`} loading={testRunLoading} />
      <PlaywrightInfoCard projectId={projectId} />
      {securityEnabled && <SecurityInfoCard projectId={projectId} />}
    </div>
  )
}

// Issue #4032: this card subscribed to its query with a fixed 15s pollingInterval. When the
// endpoint answers with a deterministic denial — 403 {"error":"Access denied"} for a project this
// session cannot read, or a 401 — no retry can succeed, yet the poll kept re-firing every ~15s for
// the whole session (10 identical doomed requests in the recorded one, ~533ms each, none cached)
// while the result was never rendered. apiFetch only special-cases 401 for a login redirect, so
// nothing stopped the loop from the fetch layer either. The denial is therefore latched per project
// in the card and pollingInterval drops to 0 until the component is reused for another project;
// transient failures (network errors, 5xx) keep polling.
const PLAYWRIGHT_ANALYSES_POLL_MS = 15_000

export function PlaywrightInfoCard({ projectId }: { projectId: string }) {
  const user = useSelector((s: RootState) => s.auth.user)
  // The failure-analyses endpoint is token/session-gated and the browser holds no Authorization
  // credential: a signed-out visitor has no session cookie either, so every poll it sends is a
  // doomed 401 and the card could never render for them anyway. Skip both the settings read and
  // the poll while signed out. For signed-in users, issue #4032: RTK Query hook options cannot
  // read the query's own error state, so a deterministic 401/403 denial is latched in local
  // state and the effect below flips the interval on the render after the error; transient
  // failures (network errors, 5xx) keep polling.
  const settings = useGetUserSettingsQuery(undefined, { skip: !user })
  const [deniedProjectId, setDeniedProjectId] = useState<string | null>(null)
  const analyses = useGetPlaywrightAnalysesQuery({ projectId, limit: 1 }, {
    skip: !user,
    skipPollingIfUnfocused: true,
    pollingInterval: deniedProjectId === projectId ? 0 : PLAYWRIGHT_ANALYSES_POLL_MS,
  })
  useEffect(() => {
    if (permanentHttpDenial(analyses.error)) setDeniedProjectId(projectId)
  }, [analyses.error, projectId])
  const playwrightEnabled = settings.data?.experimental_settings.playwright_tests === true || (analyses.currentData?.total ?? 0) > 0
  if (!playwrightEnabled) return null
  return (
    <StatCard
      icon={<ListChecks size={18} />}
      label="Playwright Tests · Experimental"
      to={`/projects/${projectId}/playwright-tests`}
      loading={analyses.isLoading}
      loadingValue={<span className="overview-summary__card-cta">Connect CI to analyze failures <ArrowRight size={16} /></span>}
      value={<span className="overview-summary__card-cta">{analyses.isError ? 'Analysis results unavailable' : analyses.currentData?.total
        ? `${analyses.currentData.processing} processing · ${analyses.currentData.completed} analyzed · ${analyses.currentData.failed} failed`
        : 'Connect CI to analyze failures'} <ArrowRight size={16} /></span>}
    />
  )
}

export function SecurityInfoCard({ projectId }: { projectId: string }) {
  const query = useGetProjectSecurityResultsQuery({ projectId }, { pollingInterval: 60_000, skipPollingIfUnfocused: true })
  const latest = query.currentData?.scans[0]
  return <StatCard
    icon={<ShieldCheck size={18} />}
    label="Security"
    to={`/projects/${projectId}/security`}
    loading={query.isLoading}
    loadingValue={<span className="overview-summary__card-cta">No security scans yet <ArrowRight size={16} /></span>}
    value={query.isError ? <span className="overview-summary__card-cta">Security results unavailable</span>
      : latest ? <span className="overview-summary__security-result">
        <span className="overview-summary__security-counts">
          <span className={`overview-summary__security-metric${latest.security_bug_count ? ' overview-summary__security-metric--bugs' : ''}`} aria-label={`${latest.security_bug_count} security bug${latest.security_bug_count === 1 ? '' : 's'}`} title="Security bugs">{latest.security_bug_count}<Bug size={18} aria-hidden /></span>
          <span className={`overview-summary__security-metric${latest.warnings.length ? ' overview-summary__security-metric--warnings' : ''}`} aria-label={`${latest.warnings.length} warning${latest.warnings.length === 1 ? '' : 's'}`} title="Warnings">{latest.warnings.length}<AlertTriangle size={18} aria-hidden /></span>
        </span>
        <span className="overview-summary__security-date">{latest.completed_at
          ? <>Scan finished <time dateTime={latest.completed_at}>{new Date(latest.completed_at).toLocaleString()}</time></>
          : 'Security scan in progress'}</span>
      </span>
        : <span className="overview-summary__card-cta">No security scans yet <ArrowRight size={16} /></span>}
  />
}

const SEVERITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 }

const OVERVIEW_BUGS_LIMIT = 5

// The top open bugs shown on the overview next to the breakdown chart, in the same table the Bugs tab
// uses — trimmed to the highest-severity open bugs, and without the Environment column, which doesn't
// fit beside the chart. The full, filterable table lives on the Bugs tab.
export function OverviewBugTable({ bugs, projectId }: { bugs: BugType[]; projectId: string }) {
  const openBugs = useMemo(
    () =>
      bugs
        .filter(b => !isResolved(b.status))
        .sort((a, b) => (SEVERITY_RANK[a.severity] ?? 99) - (SEVERITY_RANK[b.severity] ?? 99))
        .slice(0, OVERVIEW_BUGS_LIMIT),
    [bugs],
  )

  if (openBugs.length === 0) return null

  return (
    <div className="overview-summary__bug-table-wrap" data-testid="overview-bug-table">
      <BugTable bugs={openBugs} projectId={projectId} loading={false} hasWebhook={false} showEnvironment={false} />
    </div>
  )
}

const OVERVIEW_RUNS_LIMIT = 5

// The overview's Test Runs section: the most recent runs in the same table the Test Runs tab uses,
// under a "Test Runs · View all →" header with a search box that filters the shown runs.
export function OverviewTestRuns({ projectId, runs }: { projectId: string; runs: TestSession[] }) {
  const [search, setSearch] = useState('')

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    const matched = q
      ? runs.filter(r =>
          r.title.toLowerCase().includes(q) ||
          r.status.toLowerCase().includes(q) ||
          r.journeyRuns.some(j => (j.journey_name ?? j.goal ?? '').toLowerCase().includes(q)),
        )
      : runs
    return matched.slice(0, OVERVIEW_RUNS_LIMIT)
  }, [runs, search])

  if (runs.length === 0) return null

  return (
    <section className="overview-summary__runs" data-testid="overview-test-runs">
      <div className="overview-summary__bugs-head">
        <h2 className="overview-summary__bugs-title">Test Runs</h2>
        <div className="overview-summary__runs-head-right">
          <label className="overview-summary__runs-search">
            <Search size={14} aria-hidden />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search"
              aria-label="Search test runs"
              data-testid="overview-test-run-search"
            />
          </label>
          <Link
            to={`/projects/${projectId}/test-runs`}
            className="overview-summary__bugs-viewall"
            data-analytics-event="project-overview-view-all-test-runs"
          >
            View all
            <ArrowRight size={15} aria-hidden />
          </Link>
        </div>
      </div>
      {visible.length > 0 ? (
        <TestRunTable projectId={projectId} sessions={visible} />
      ) : (
        <p className="overview-summary__runs-empty">No test runs match “{search.trim()}”.</p>
      )}
    </section>
  )
}

// Section header for the unresolved-bugs list: title + count badge. The "Bugs" stat card above and
// the persistent header tab already link to the same /bugs route under the same name, so a second
// CTA here would be one destination under a third label (QA bug bug-mtz79gp6-tjy6, issue #4038).
export function UnresolvedBugsHeader({ count }: { count: number }) {
  return (
    <div className="overview-summary__bugs-head">
      <h2 className="overview-summary__bugs-title">
        Bugs
        {count > 0 && <span className="overview-summary__bugs-count">{count}</span>}
      </h2>
    </div>
  )
}
